// Particles anchored to places on the map. Each anchor (a battle's location) is projected once per frame; its
// particles live in a small local frame around it (x east, y north, z up, in "units" that grow gently with zoom),
// so they stay put while the camera moves, and smoke rises "up" even on a tilted map.

import { STRIDE } from "./renderer";
import { S_ASH, S_DEBRIS, S_DUST, S_EMBER, S_FLAME, S_FLASH, S_GLOW, S_RING, S_SCORCH, S_SHOCK, S_SMOKE, S_SPLASH, S_STAR, S_STREAK } from "./sprites";
import { ERA_BLADE, ERA_POWDER, ERA_RIFLE } from "./logic";

export type RGBA = [number, number, number, number];

// draw layers, bottom to top
export const L_GROUND = 0; // scorch marks, ripples (normal blending)
export const L_FIRE = 1; // fireballs and flames (additive), under the smoke
export const L_SMOKE = 2; // smoke, dust, debris, ash (normal)
export const L_LIGHT = 3; // flashes, sparks, shockwaves, embers (additive)
export const LAYERS = 4;
export const ADDITIVE = [false, true, false, true];

export const F_GROUND = 1; // lies on the ground: foreshortened with the camera's tilt
export const F_STRETCH = 2; // drawn along its screen velocity (sparks)
export const F_WIND = 4; // drifts with the wind (smoke)
export const F_FLICKER = 8;
export const F_BASE = 16; // the sprite stands on its point (flames, splashes) rather than centring on it
export const F_BOUNCE = 32; // falls to the ground and bounces (debris)

// alpha over life
export const FADE_OUT = 0, FADE_PUFF = 1, FADE_FLASH = 2, FADE_BUMP = 3, FADE_HOLD = 4;

/** The camera, as the particles need it. */
export interface View {
  w: number;
  h: number;
  unit: number; // px per unit at the centre of the view
  cosB: number;
  sinB: number;
  cosP: number; // ground foreshortening (tilt)
  zf: number; // screen px per unit of height (stylised: smoke rises visibly even seen from straight above)
}

export class Anchors {
  static readonly CAP = 768;
  readonly lon = new Float64Array(Anchors.CAP);
  readonly lat = new Float64Array(Anchors.CAP);
  readonly until = new Float64Array(Anchors.CAP); // simulation time (s) after which it may be reused
  readonly used = new Uint8Array(Anchors.CAP);
  readonly sx = new Float32Array(Anchors.CAP);
  readonly sy = new Float32Array(Anchors.CAP);
  readonly scale = new Float32Array(Anchors.CAP); // perspective: units shrink towards the horizon
  readonly on = new Uint8Array(Anchors.CAP); // projected near the screen
  private free: number[] = [];

  constructor() {
    for (let i = Anchors.CAP - 1; i >= 0; i--) this.free.push(i);
  }

  add(lon: number, lat: number, until: number): number {
    const i = this.free.pop();
    if (i == null) return -1;
    this.lon[i] = lon;
    this.lat[i] = lat;
    this.until[i] = until;
    this.used[i] = 1;
    this.on[i] = 0;
    return i;
  }

  keep(i: number, until: number) {
    if (i >= 0 && until > this.until[i]) this.until[i] = until;
  }

  /** Project every anchor in use; release those nothing needs any more. */
  project(project: (lon: number, lat: number) => [number, number], view: View, centerLon: number, centerLat: number, zoom: number, now: number) {
    const d = (24 * 360) / (512 * Math.pow(2, zoom)); // ~24 px of longitude at this zoom, for the local scale
    // the reference is the centre of the view: on a tilted map, places nearer the camera draw bigger
    const [cx0, cy0] = project(centerLon, centerLat);
    const [cx1, cy1] = project(centerLon + d, centerLat);
    const ref = Math.hypot(cx1 - cx0, cy1 - cy0) || 1;
    const m = Math.max(view.w, view.h) * 0.6;
    for (let i = 0; i < Anchors.CAP; i++) {
      if (!this.used[i]) continue;
      if (now > this.until[i]) {
        this.used[i] = 0;
        this.on[i] = 0;
        this.free.push(i);
        continue;
      }
      const lon = this.lon[i] + 360 * Math.round((centerLon - this.lon[i]) / 360); // the world copy in view
      const [x, y] = project(lon, this.lat[i]);
      this.sx[i] = x;
      this.sy[i] = y;
      const on = x > -m && x < view.w + m && y > -m && y < view.h + m && Number.isFinite(x) && Number.isFinite(y);
      this.on[i] = on ? 1 : 0;
      if (on) {
        const [x1, y1] = project(lon + d, this.lat[i]);
        this.scale[i] = Math.min(2.5, Math.max(0.3, Math.hypot(x1 - x, y1 - y) / ref));
      }
    }
  }
}

export interface Spawn {
  a: number; // anchor
  sprite: number;
  layer: number;
  flags?: number;
  fade?: number;
  life: number; // seconds
  delay?: number; // seconds before it appears
  x?: number; y?: number; z?: number;
  vx?: number; vy?: number; vz?: number;
  drag?: number; // per second
  grav?: number; // units/s² on z (negative falls, positive rises)
  s0: number; s1: number; // size (units) at birth and death
  stretch?: number;
  rot?: number; vr?: number;
  c0: RGBA; c1?: RGBA;
}

export class Particles {
  n = 0;
  readonly cap: number;
  readonly anchor: Int16Array; readonly sprite: Uint8Array; readonly layer: Uint8Array; readonly flags: Uint8Array;
  readonly fade: Uint8Array;
  readonly age: Float32Array; readonly life: Float32Array;
  readonly x: Float32Array; readonly y: Float32Array; readonly z: Float32Array;
  readonly vx: Float32Array; readonly vy: Float32Array; readonly vz: Float32Array;
  readonly drag: Float32Array; readonly grav: Float32Array;
  readonly s0: Float32Array; readonly s1: Float32Array; readonly stretch: Float32Array;
  readonly rot: Float32Array; readonly vr: Float32Array;
  readonly c0: Float32Array; readonly c1: Float32Array;
  readonly out: Float32Array[];
  readonly counts = new Int32Array(LAYERS);

  constructor(cap: number) {
    this.cap = cap;
    this.anchor = new Int16Array(cap); this.sprite = new Uint8Array(cap); this.layer = new Uint8Array(cap);
    this.flags = new Uint8Array(cap); this.fade = new Uint8Array(cap);
    this.age = new Float32Array(cap); this.life = new Float32Array(cap);
    this.x = new Float32Array(cap); this.y = new Float32Array(cap); this.z = new Float32Array(cap);
    this.vx = new Float32Array(cap); this.vy = new Float32Array(cap); this.vz = new Float32Array(cap);
    this.drag = new Float32Array(cap); this.grav = new Float32Array(cap);
    this.s0 = new Float32Array(cap); this.s1 = new Float32Array(cap); this.stretch = new Float32Array(cap);
    this.rot = new Float32Array(cap); this.vr = new Float32Array(cap);
    this.c0 = new Float32Array(cap * 4); this.c1 = new Float32Array(cap * 4);
    this.out = Array.from({ length: LAYERS }, () => new Float32Array(cap * STRIDE));
  }

  get full() { return this.n >= this.cap; }

  spawn(p: Spawn): number {
    if (this.n >= this.cap || p.a < 0) return -1;
    const i = this.n++;
    this.anchor[i] = p.a; this.sprite[i] = p.sprite; this.layer[i] = p.layer; this.flags[i] = p.flags ?? 0;
    this.fade[i] = p.fade ?? FADE_OUT;
    this.age[i] = -(p.delay ?? 0); this.life[i] = Math.max(0.01, p.life);
    this.x[i] = p.x ?? 0; this.y[i] = p.y ?? 0; this.z[i] = p.z ?? 0;
    this.vx[i] = p.vx ?? 0; this.vy[i] = p.vy ?? 0; this.vz[i] = p.vz ?? 0;
    this.drag[i] = p.drag ?? 0; this.grav[i] = p.grav ?? 0;
    this.s0[i] = p.s0; this.s1[i] = p.s1; this.stretch[i] = p.stretch ?? 0;
    this.rot[i] = p.rot ?? 0; this.vr[i] = p.vr ?? 0;
    const c1 = p.c1 ?? p.c0;
    for (let k = 0; k < 4; k++) { this.c0[i * 4 + k] = p.c0[k]; this.c1[i * 4 + k] = c1[k]; }
    return i;
  }

  /** Move everything on by dt seconds; drop what has lived its life (swap with the last). */
  update(dt: number, windX: number, windY: number) {
    for (let i = 0; i < this.n; i++) {
      const age = (this.age[i] += dt);
      if (age >= this.life[i]) {
        this.kill(i);
        i--;
        continue;
      }
      if (age < 0) continue;
      const k = Math.exp(-this.drag[i] * dt);
      this.vx[i] *= k;
      this.vy[i] *= k;
      this.vz[i] = this.vz[i] * k + this.grav[i] * dt;
      this.x[i] += this.vx[i] * dt;
      this.y[i] += this.vy[i] * dt;
      this.z[i] += this.vz[i] * dt;
      this.rot[i] += this.vr[i] * dt;
      const f = this.flags[i];
      if (f & F_WIND) {
        const h = 0.4 + Math.min(1.6, this.z[i] / 40); // stronger aloft
        this.x[i] += windX * h * dt;
        this.y[i] += windY * h * dt;
      }
      if (f & F_BOUNCE && this.z[i] < 0) {
        this.z[i] = 0;
        this.vz[i] = Math.abs(this.vz[i]) * 0.28;
        this.vx[i] *= 0.55;
        this.vy[i] *= 0.55;
      }
    }
  }

  private kill(i: number) {
    const j = --this.n;
    if (i === j) return;
    this.anchor[i] = this.anchor[j]; this.sprite[i] = this.sprite[j]; this.layer[i] = this.layer[j];
    this.flags[i] = this.flags[j]; this.fade[i] = this.fade[j];
    this.age[i] = this.age[j]; this.life[i] = this.life[j];
    this.x[i] = this.x[j]; this.y[i] = this.y[j]; this.z[i] = this.z[j];
    this.vx[i] = this.vx[j]; this.vy[i] = this.vy[j]; this.vz[i] = this.vz[j];
    this.drag[i] = this.drag[j]; this.grav[i] = this.grav[j];
    this.s0[i] = this.s0[j]; this.s1[i] = this.s1[j]; this.stretch[i] = this.stretch[j];
    this.rot[i] = this.rot[j]; this.vr[i] = this.vr[j];
    for (let k = 0; k < 4; k++) { this.c0[i * 4 + k] = this.c0[j * 4 + k]; this.c1[i * 4 + k] = this.c1[j * 4 + k]; }
  }

  clear() { this.n = 0; }

  /** Write the visible particles into the per-layer instance buffers. */
  build(A: Anchors, v: View, time: number) {
    this.counts.fill(0);
    for (let i = 0; i < this.n; i++) {
      const age = this.age[i];
      if (age < 0) continue;
      const a = this.anchor[i];
      if (!A.on[a]) continue;
      const u = age / this.life[i];
      const fade = this.fade[i];
      let alpha = fade === FADE_OUT ? 1 - u
        : fade === FADE_PUFF ? Math.min(1, u * 9) * Math.pow(1 - u, 1.3)
          : fade === FADE_FLASH ? Math.pow(1 - u, 2.4)
            : fade === FADE_BUMP ? Math.sin(Math.PI * u)
              : u < 0.72 ? Math.min(1, u * 12) : (1 - u) / 0.28;
      const f = this.flags[i];
      if (f & F_FLICKER) alpha *= 0.72 + 0.28 * Math.sin(age * 29 + i * 1.7) * Math.sin(age * 13.3 + i);
      const ci = i * 4;
      const ca = this.c0[ci + 3] + (this.c1[ci + 3] - this.c0[ci + 3]) * u;
      alpha *= ca;
      if (alpha <= 0.004) continue;
      const U = v.unit * A.scale[a];
      const gx = this.x[i] * v.cosB - this.y[i] * v.sinB;
      const gy = this.x[i] * v.sinB + this.y[i] * v.cosB;
      let sx = A.sx[a] + U * gx;
      let sy = A.sy[a] - U * (gy * v.cosP + this.z[i] * v.zf);
      const e = 1 - (1 - u) * (1 - u);
      const size = (this.s0[i] + (this.s1[i] - this.s0[i]) * e) * U * 0.5;
      if (size < 0.25) continue;
      let hx = size, hy = size, rot = this.rot[i];
      if (f & F_GROUND) hy = size * v.cosP;
      if (f & F_BASE) sy -= hy * 0.85;
      if (f & F_STRETCH) {
        const svx = U * (this.vx[i] * v.cosB - this.vy[i] * v.sinB);
        const svy = -U * ((this.vx[i] * v.sinB + this.vy[i] * v.cosB) * v.cosP + this.vz[i] * v.zf);
        const sp = Math.hypot(svx, svy);
        rot = Math.atan2(svy, svx);
        hx = Math.max(size, sp * this.stretch[i] * 0.5);
        hy = size * 0.45;
        sx -= Math.cos(rot) * (hx - size) * 0.5;
        sy -= Math.sin(rot) * (hx - size) * 0.5;
      }
      if (sx + hx < -50 || sx - hx > v.w + 50 || sy + hy < -50 || sy - hy > v.h + 50) continue;
      const L = this.layer[i];
      const buf = this.out[L];
      const o = this.counts[L]++ * STRIDE;
      buf[o] = sx; buf[o + 1] = sy; buf[o + 2] = hx; buf[o + 3] = hy;
      buf[o + 4] = rot; buf[o + 5] = this.sprite[i];
      const cu = u < 1 ? u : 1;
      buf[o + 6] = this.c0[ci] + (this.c1[ci] - this.c0[ci]) * cu;
      buf[o + 7] = this.c0[ci + 1] + (this.c1[ci + 1] - this.c0[ci + 1]) * cu;
      buf[o + 8] = this.c0[ci + 2] + (this.c1[ci + 2] - this.c0[ci + 2]) * cu;
      buf[o + 9] = alpha > 1 ? 1 : alpha;
    }
    void time;
  }
}

// --- the effects ------------------------------------------------------------------------------------------

const R = Math.random;
const rr = (a: number, b: number) => a + (b - a) * R();
const pick = <T,>(xs: T[]) => xs[Math.floor(R() * xs.length)];
const SMOKES = [S_SMOKE, S_SMOKE + 1, S_SMOKE + 2];

const WHITE: RGBA = [1, 0.97, 0.88, 1];
const FIRE: RGBA = [1, 0.82, 0.42, 1];
const FIRE_END: RGBA = [0.75, 0.14, 0.04, 0];
const SPARK: RGBA = [1, 0.78, 0.38, 1];
const SPARK_END: RGBA = [1, 0.28, 0.05, 0];
const DARK_SMOKE: RGBA = [0.12, 0.11, 0.1, 0.72];
const DARK_SMOKE_END: RGBA = [0.24, 0.23, 0.22, 0];
const POWDER_SMOKE: RGBA = [0.86, 0.84, 0.79, 0.5];
const POWDER_SMOKE_END: RGBA = [0.66, 0.66, 0.64, 0];
const GRAY_SMOKE: RGBA = [0.45, 0.43, 0.41, 0.6];
const GRAY_SMOKE_END: RGBA = [0.5, 0.49, 0.48, 0];
const DUST: RGBA = [0.68, 0.6, 0.47, 0.7];
const DUST_END: RGBA = [0.56, 0.5, 0.42, 0];
const SHOCK: RGBA = [1, 0.84, 0.6, 0.75];
const SHOCK_END: RGBA = [1, 0.5, 0.2, 0];
const SCORCH: RGBA = [0.05, 0.04, 0.035, 0.6];
const EMBER: RGBA = [1, 0.66, 0.24, 1];
const EMBER_END: RGBA = [1, 0.24, 0.04, 0];
const VIOLET: RGBA = [0.66, 0.52, 0.96, 0.6];
const VIOLET_END: RGBA = [0.5, 0.36, 0.86, 0];
const ASH: RGBA = [0.66, 0.64, 0.62, 0.75];
const WATER: RGBA = [0.86, 0.93, 1, 0.75];
const WATER_END: RGBA = [0.7, 0.82, 0.95, 0];

/** Size factor of an event of magnitude m (0–1). */
const sizeOf = (m: number) => 0.55 + 0.9 * m;

function smokeColors(era: number): [RGBA, RGBA] {
  return era <= ERA_POWDER ? [POWDER_SMOKE, POWDER_SMOKE_END] : era === ERA_RIFLE ? [GRAY_SMOKE, GRAY_SMOKE_END] : [DARK_SMOKE, DARK_SMOKE_END];
}

/** A high-explosive blast: flash, fireball, sparks and debris, a shockwave on the ground, smoke, a scorch mark. */
export function blast(P: Particles, a: number, m: number, q: number, o: { x?: number; y?: number; delay?: number; scorch?: boolean; era?: number } = {}) {
  const k = sizeOf(m), x = o.x ?? 0, y = o.y ?? 0, d = o.delay ?? 0;
  const [sc0, sc1] = smokeColors(o.era ?? 4);
  P.spawn({ a, sprite: S_GLOW, layer: L_LIGHT, fade: FADE_FLASH, life: 0.3, delay: d, x, y, z: 4 * k, s0: 40 * k, s1: 88 * k, c0: WHITE, c1: [1, 0.7, 0.35, 0.6] });
  P.spawn({ a, sprite: S_FLASH, layer: L_LIGHT, fade: FADE_FLASH, life: 0.16, delay: d, x, y, z: 4 * k, s0: 48 * k, s1: 72 * k, rot: rr(0, 6.28), c0: WHITE });
  for (let n = Math.round((5 + 8 * m) * q) + 1; n > 0; n--) {
    P.spawn({ a, sprite: pick([S_GLOW, S_FLAME]), layer: L_FIRE, fade: FADE_PUFF, life: rr(0.55, 1.1), delay: d + rr(0, 0.08),
      x: x + rr(-5, 5) * k, y: y + rr(-5, 5) * k, z: rr(2, 7) * k, vx: rr(-16, 16) * k, vy: rr(-16, 16) * k, vz: rr(8, 22) * k,
      drag: 2.6, s0: rr(12, 20) * k, s1: rr(36, 58) * k, rot: rr(0, 6.28), c0: FIRE, c1: FIRE_END });
  }
  for (let n = Math.round((14 + 34 * m) * q); n > 0; n--) {
    const ang = rr(0, 6.28), sp = rr(70, 190) * k;
    P.spawn({ a, sprite: S_STREAK, layer: L_LIGHT, flags: F_STRETCH, life: rr(0.45, 1.15), delay: d, x, y, z: 3 * k,
      vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp, vz: rr(25, 105) * k, drag: 1.6, grav: -140, stretch: 0.07,
      s0: 3.2, s1: 1.6, c0: SPARK, c1: SPARK_END });
  }
  for (let n = Math.round((3 + 8 * m) * q); n > 0; n--) {
    const ang = rr(0, 6.28), sp = rr(30, 90) * k;
    P.spawn({ a, sprite: S_DEBRIS, layer: L_SMOKE, flags: F_BOUNCE, life: rr(0.9, 1.6), delay: d, x, y, z: 2,
      vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp, vz: rr(30, 90) * k, drag: 1.2, grav: -160, rot: rr(0, 6.28), vr: rr(-9, 9),
      s0: rr(1.6, 3) * k, s1: rr(1.4, 2.4) * k, c0: [0.09, 0.07, 0.06, 0.9], c1: [0.09, 0.07, 0.06, 0] });
  }
  for (let n = Math.round((6 + 14 * m) * q) + 1; n > 0; n--) {
    P.spawn({ a, sprite: pick(SMOKES), layer: L_SMOKE, flags: F_WIND, fade: FADE_PUFF, life: rr(3.5, 7) * (0.8 + m * 0.5),
      delay: d + rr(0.05, 0.45), x: x + rr(-6, 6) * k, y: y + rr(-6, 6) * k, z: rr(4, 12) * k, vx: rr(-9, 9) * k, vy: rr(-9, 9) * k,
      vz: rr(6, 18) * k, drag: 0.7, s0: rr(14, 22) * k, s1: rr(56, 92) * k, rot: rr(0, 6.28), vr: rr(-0.3, 0.3), c0: sc0, c1: sc1 });
  }
  P.spawn({ a, sprite: S_SHOCK, layer: L_LIGHT, flags: F_GROUND, life: 0.65, delay: d, x, y, s0: 8 * k, s1: 150 * k, c0: SHOCK, c1: SHOCK_END });
  P.spawn({ a, sprite: S_DUST, layer: L_SMOKE, flags: F_GROUND | F_WIND, fade: FADE_PUFF, life: 2, delay: d + 0.05, x, y, z: 1,
    s0: 18 * k, s1: 112 * k, c0: [0.4, 0.36, 0.3, 0.4], c1: [0.4, 0.36, 0.3, 0] });
  if (o.scorch !== false) {
    P.spawn({ a, sprite: S_SCORCH, layer: L_GROUND, flags: F_GROUND, fade: FADE_HOLD, life: rr(18, 28), delay: d + 0.1, x, y,
      s0: 40 * k, s1: 46 * k, rot: rr(0, 6.28), c0: SCORCH });
  }
  for (let n = Math.round((5 + 12 * m) * q); n > 0; n--) {
    P.spawn({ a, sprite: S_EMBER, layer: L_LIGHT, flags: F_FLICKER | F_WIND, life: rr(1, 2.4), delay: d + rr(0.1, 0.5),
      x: x + rr(-10, 10) * k, y: y + rr(-10, 10) * k, z: rr(4, 12) * k, vx: rr(-12, 12), vy: rr(-12, 12), vz: rr(12, 34), drag: 1.1,
      grav: 3, s0: 2.6, s1: 1.2, c0: EMBER, c1: EMBER_END });
  }
}

/** Black-powder battle: a line of muzzle flashes, banks of white smoke, roundshot kicking up earth. */
export function volleyLine(P: Particles, a: number, m: number, q: number, era: number) {
  const k = sizeOf(m), ang = rr(0, 3.14), len = (30 + 40 * m) * k, ux = Math.cos(ang), uy = Math.sin(ang);
  const [sc0, sc1] = smokeColors(era);
  const n = Math.round((6 + 12 * m) * q) + 2;
  for (let j = 0; j < n; j++) {
    const f = rr(-0.5, 0.5), x = ux * len * f + rr(-3, 3), y = uy * len * f + rr(-3, 3), d = rr(0, 1.1);
    P.spawn({ a, sprite: S_FLASH, layer: L_LIGHT, fade: FADE_FLASH, life: 0.18, delay: d, x, y, z: 2, s0: 13 * k, s1: 21 * k,
      rot: rr(0, 6.28), c0: [1, 0.86, 0.56, 1] });
    P.spawn({ a, sprite: pick(SMOKES), layer: L_SMOKE, flags: F_WIND, fade: FADE_PUFF, life: rr(4, 8), delay: d + 0.04, x, y, z: 3,
      vx: rr(-3, 3) - uy * 4, vy: rr(-3, 3) + ux * 4, vz: rr(2, 6), drag: 0.6, s0: 9 * k, s1: rr(36, 56) * k, rot: rr(0, 6.28),
      vr: rr(-0.2, 0.2), c0: sc0, c1: sc1 });
  }
  // roundshot and shells landing on the other side of the line
  for (let j = Math.round((3 + 6 * m) * q); j > 0; j--) {
    const f = rr(-0.5, 0.5), side = rr(10, 22) * k, x = ux * len * f - uy * side, y = uy * len * f + ux * side, d = rr(0.25, 1.5);
    P.spawn({ a, sprite: S_DUST, layer: L_SMOKE, flags: F_WIND, fade: FADE_PUFF, life: rr(1, 1.8), delay: d, x, y, z: 2, vz: rr(6, 14),
      s0: 5 * k, s1: rr(18, 30) * k, c0: DUST, c1: DUST_END });
    if (era >= ERA_RIFLE) blastSmall(P, a, m * 0.5, q, x, y, d);
  }
  if (m > 0.55) P.spawn({ a, sprite: S_SCORCH, layer: L_GROUND, flags: F_GROUND, fade: FADE_HOLD, life: 16, x: 0, y: 0, s0: len * 0.8, s1: len * 0.85, rot: ang, c0: [0.08, 0.06, 0.05, 0.3] });
}

function blastSmall(P: Particles, a: number, m: number, q: number, x: number, y: number, delay: number) {
  const k = sizeOf(m) * 0.8;
  P.spawn({ a, sprite: S_GLOW, layer: L_LIGHT, fade: FADE_FLASH, life: 0.22, delay, x, y, z: 2, s0: 22 * k, s1: 50 * k, c0: WHITE, c1: [1, 0.6, 0.3, 0.5] });
  P.spawn({ a, sprite: S_GLOW, layer: L_FIRE, fade: FADE_PUFF, life: 0.6, delay, x, y, z: 3, vz: 12, s0: 10 * k, s1: 26 * k, c0: FIRE, c1: FIRE_END });
  for (let n = Math.round(6 * q) + 2; n > 0; n--) {
    const ang = rr(0, 6.28), sp = rr(40, 100) * k;
    P.spawn({ a, sprite: S_STREAK, layer: L_LIGHT, flags: F_STRETCH, life: rr(0.3, 0.7), delay, x, y, z: 2, vx: Math.cos(ang) * sp,
      vy: Math.sin(ang) * sp, vz: rr(20, 60) * k, drag: 1.8, grav: -140, stretch: 0.06, s0: 2.2, s1: 1.2, c0: SPARK, c1: SPARK_END });
  }
  P.spawn({ a, sprite: pick(SMOKES), layer: L_SMOKE, flags: F_WIND, fade: FADE_PUFF, life: rr(2.5, 4), delay: delay + 0.1, x, y, z: 4,
    vz: rr(6, 12), drag: 0.6, s0: 10 * k, s1: 42 * k, rot: rr(0, 6.28), c0: DARK_SMOKE, c1: DARK_SMOKE_END });
}

/** Before gunpowder: dust over the lines, arrow volleys arcing between them, the glint of steel. */
export function melee(P: Particles, a: number, m: number, q: number) {
  const k = sizeOf(m), ang = rr(0, 3.14), len = (26 + 34 * m) * k, ux = Math.cos(ang), uy = Math.sin(ang);
  for (let j = Math.round((8 + 12 * m) * q) + 2; j > 0; j--) {
    const f = rr(-0.55, 0.55);
    P.spawn({ a, sprite: pick([S_DUST, S_SMOKE]), layer: L_SMOKE, flags: F_WIND, fade: FADE_PUFF, life: rr(2.5, 5.5), delay: rr(0, 1.4),
      x: ux * len * f + rr(-5, 5), y: uy * len * f + rr(-5, 5), z: 2, vx: rr(-4, 4), vy: rr(-4, 4), vz: rr(2, 5), drag: 0.7,
      s0: 12 * k, s1: rr(44, 70) * k, rot: rr(0, 6.28), c0: DUST, c1: DUST_END });
  }
  // arrow volleys: pale streaks arcing from behind one line onto the other
  for (let j = Math.round((10 + 22 * m) * q); j > 0; j--) {
    const f = rr(-0.5, 0.5), side = rr(18, 30) * k * (R() < 0.5 ? 1 : -1);
    const x = ux * len * f - uy * side, y = uy * len * f + ux * side, t = rr(0.8, 1.1);
    P.spawn({ a, sprite: S_STREAK, layer: L_SMOKE, flags: F_STRETCH, life: t, delay: rr(0, 1.6), x, y, z: 2,
      vx: (uy * side * 2) / t, vy: (-ux * side * 2) / t, vz: 55, grav: -110, stretch: 0.05, s0: 2.4, s1: 2.4,
      c0: [0.9, 0.84, 0.72, 0.8], c1: [0.9, 0.84, 0.72, 0.5] });
  }
  for (let j = Math.round((14 + 30 * m) * q); j > 0; j--) {
    const f = rr(-0.5, 0.5);
    P.spawn({ a, sprite: S_STAR, layer: L_LIGHT, fade: FADE_FLASH, life: rr(0.12, 0.3), delay: rr(0.3, 2.8), x: ux * len * f + rr(-4, 4),
      y: uy * len * f + rr(-4, 4), z: rr(1, 4), s0: rr(8, 14), s1: rr(10, 17), rot: rr(0, 1), c0: [1, 0.96, 0.84, 0.95] });
  }
  P.spawn({ a, sprite: S_SCORCH, layer: L_GROUND, flags: F_GROUND, fade: FADE_HOLD, life: 14, s0: len, s1: len * 1.05, rot: ang, c0: [0.1, 0.08, 0.05, 0.25] });
}

/** Ships in action: columns of spray, rings on the water, gun flashes, burning hulls. */
export function naval(P: Particles, a: number, m: number, q: number, era: number) {
  const k = sizeOf(m);
  for (let j = Math.round((3 + 7 * m) * q) + 1; j > 0; j--) {
    const x = rr(-24, 24) * k, y = rr(-16, 16) * k, d = rr(0, 1.6);
    P.spawn({ a, sprite: S_SPLASH, layer: L_SMOKE, flags: F_BASE, fade: FADE_BUMP, life: rr(0.9, 1.4), delay: d, x, y, s0: 11 * k, s1: rr(22, 34) * k, c0: WATER, c1: WATER_END });
    P.spawn({ a, sprite: S_RING, layer: L_GROUND, flags: F_GROUND, life: rr(1.6, 2.4), delay: d, x, y, s0: 4 * k, s1: rr(36, 56) * k, c0: [0.8, 0.9, 1, 0.32], c1: [0.7, 0.85, 1, 0] });
  }
  if (era >= ERA_POWDER) volleyLine(P, a, m * 0.8, q * 0.7, era);
  if (era > ERA_RIFLE) blast(P, a, m * 0.75, q * 0.8, { scorch: false, x: rr(-10, 10), y: rr(-10, 10), delay: rr(0.2, 0.8) });
}

/** The fall of a bomb load: explosions walking in a line. */
export function bombing(P: Particles, a: number, m: number, q: number) {
  const ang = rr(0, 6.28), ux = Math.cos(ang), uy = Math.sin(ang), n = Math.round(5 + 7 * m * q), len = (50 + 50 * m);
  for (let j = 0; j < n; j++) {
    const f = j / Math.max(1, n - 1) - 0.5;
    blast(P, a, m * 0.45, q * 0.55, { x: ux * len * f + rr(-5, 5), y: uy * len * f + rr(-5, 5), delay: j * 0.13 + rr(0, 0.05), scorch: j % 2 === 0 });
  }
}

/** Massacres, deportations, violence against civilians: a slow ring, a dim glow, falling ash. No blast. */
export function memorial(P: Particles, a: number, m: number, q: number) {
  const k = sizeOf(m);
  for (let j = 0; j < 2; j++) {
    P.spawn({ a, sprite: S_RING, layer: L_GROUND, flags: F_GROUND, fade: FADE_OUT, life: 3.6, delay: j * 0.8, s0: 8 * k, s1: 84 * k, c0: VIOLET, c1: VIOLET_END });
  }
  P.spawn({ a, sprite: S_GLOW, layer: L_FIRE, fade: FADE_BUMP, life: 2.8, z: 2, s0: 30 * k, s1: 50 * k, c0: [0.55, 0.42, 0.92, 0.36] });
  for (let j = Math.round((5 + 12 * m) * q); j > 0; j--) {
    P.spawn({ a, sprite: S_ASH, layer: L_SMOKE, flags: F_WIND, fade: FADE_BUMP, life: rr(3, 5.5), delay: rr(0, 1.5), x: rr(-18, 18) * k,
      y: rr(-18, 18) * k, z: rr(30, 60) * k, vz: rr(-12, -6) * k, drag: 0.2, rot: rr(0, 6.28), vr: rr(-1.5, 1.5), s0: 2.2, s1: 2.2, c0: ASH });
  }
}

/** Street fighting and revolts: fires breaking out, gun flashes, smoke. */
export function uprising(P: Particles, a: number, m: number, q: number, era: number) {
  const k = sizeOf(m);
  for (let j = Math.round((4 + 8 * m) * q) + 1; j > 0; j--) {
    const x = rr(-20, 20) * k, y = rr(-20, 20) * k, d = rr(0, 1.8);
    P.spawn({ a, sprite: S_STAR, layer: L_LIGHT, fade: FADE_FLASH, life: 0.12, delay: d, x, y, z: 2, s0: 5, s1: 7, rot: rr(0, 1), c0: [1, 0.9, 0.6, 1] });
  }
  if (era >= ERA_RIFLE) for (let j = Math.round(2 * m * q); j > 0; j--) blastSmall(P, a, m, q, rr(-15, 15) * k, rr(-15, 15) * k, rr(0.2, 1.5));
}

/** The atomic bombings: a blinding flash, a shockwave racing out, the mushroom cloud. */
export function nuke(P: Particles, a: number, q: number) {
  P.spawn({ a, sprite: S_GLOW, layer: L_LIGHT, fade: FADE_FLASH, life: 1.8, z: 10, s0: 120, s1: 520, c0: [1, 1, 0.96, 1], c1: [1, 0.75, 0.4, 0.7] });
  P.spawn({ a, sprite: S_GLOW, layer: L_FIRE, fade: FADE_HOLD, life: 7, z: 16, s0: 50, s1: 150, c0: [1, 0.72, 0.36, 1], c1: [0.6, 0.15, 0.05, 0] });
  P.spawn({ a, sprite: S_SHOCK, layer: L_LIGHT, flags: F_GROUND, life: 3.2, s0: 12, s1: 760, c0: [1, 0.95, 0.85, 0.9], c1: [1, 0.8, 0.6, 0] });
  P.spawn({ a, sprite: S_SHOCK, layer: L_LIGHT, flags: F_GROUND, life: 4.5, delay: 0.3, s0: 10, s1: 520, c0: [0.95, 0.85, 0.75, 0.45], c1: [0.9, 0.7, 0.5, 0] });
  P.spawn({ a, sprite: S_SCORCH, layer: L_GROUND, flags: F_GROUND, fade: FADE_HOLD, life: 40, s0: 170, s1: 190, rot: rr(0, 6.28), c0: [0.05, 0.04, 0.035, 0.7] });
  // base surge: a ring of dust rolling outward along the ground
  for (let j = Math.round(36 * q) + 8; j > 0; j--) {
    const ang = rr(0, 6.28), sp = rr(40, 70);
    P.spawn({ a, sprite: pick(SMOKES), layer: L_SMOKE, flags: F_WIND, fade: FADE_PUFF, life: rr(6, 9), delay: rr(0.2, 0.8), x: Math.cos(ang) * 20,
      y: Math.sin(ang) * 20, z: 4, vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp, drag: 0.45, s0: 26, s1: rr(70, 100), rot: rr(0, 6.28),
      vr: rr(-0.2, 0.2), c0: [0.55, 0.48, 0.42, 0.6], c1: [0.4, 0.37, 0.35, 0] });
  }
  // the stem
  for (let j = Math.round(30 * q) + 8; j > 0; j--) {
    P.spawn({ a, sprite: pick(SMOKES), layer: L_SMOKE, flags: F_WIND, fade: FADE_PUFF, life: rr(9, 12), delay: rr(0.1, 2.2), x: rr(-6, 6),
      y: rr(-6, 6), z: rr(6, 30), vz: rr(22, 32), drag: 0.18, s0: 20, s1: rr(36, 52), rot: rr(0, 6.28), vr: rr(-0.3, 0.3),
      c0: [0.95, 0.55, 0.28, 0.85], c1: [0.32, 0.29, 0.28, 0] });
  }
  // the cap: a rolling ring of cloud high above
  for (let j = Math.round(44 * q) + 10; j > 0; j--) {
    const ang = rr(0, 6.28), r0 = rr(16, 40);
    P.spawn({ a, sprite: pick(SMOKES), layer: L_SMOKE, flags: F_WIND, fade: FADE_PUFF, life: rr(10, 14), delay: rr(1.2, 3), x: Math.cos(ang) * r0,
      y: Math.sin(ang) * r0, z: rr(120, 160), vx: Math.cos(ang) * rr(5, 11), vy: Math.sin(ang) * rr(5, 11), vz: rr(5, 10), drag: 0.12,
      s0: 34, s1: rr(80, 110), rot: rr(0, 6.28), vr: rr(-0.25, 0.25), c0: [0.9, 0.5, 0.3, 0.85], c1: [0.34, 0.31, 0.3, 0] });
  }
  for (let j = Math.round(40 * q) + 10; j > 0; j--) {
    P.spawn({ a, sprite: S_FLAME, layer: L_FIRE, flags: F_BASE | F_FLICKER, life: rr(0.6, 1.2), delay: rr(0.3, 6), x: rr(-40, 40), y: rr(-40, 40),
      vz: rr(8, 16), s0: rr(10, 18), s1: rr(4, 8), c0: FIRE, c1: FIRE_END });
  }
}

/** A small flash for a minor event (budget permitting). */
export function pop(P: Particles, a: number, m: number, memorialPop: boolean) {
  const k = 0.5 + m;
  if (memorialPop) {
    P.spawn({ a, sprite: S_RING, layer: L_GROUND, flags: F_GROUND, life: 1.6, s0: 3 * k, s1: 22 * k, c0: [0.66, 0.52, 0.96, 0.45], c1: VIOLET_END });
    return;
  }
  P.spawn({ a, sprite: S_GLOW, layer: L_LIGHT, fade: FADE_FLASH, life: 0.32, z: 2, s0: 8 * k, s1: 18 * k, c0: [1, 0.85, 0.55, 0.9], c1: [1, 0.4, 0.1, 0] });
  P.spawn({ a, sprite: S_STAR, layer: L_LIGHT, fade: FADE_FLASH, life: 0.16, z: 2, s0: 10 * k, s1: 12 * k, rot: rr(0, 1), c0: WHITE });
}

/** One frame's worth of fire at a burning place (intensity 0–1), emitted at rates per second. `heavy`: the replay is
 * holding at this place (a chapter): more shells keep landing while the caption is read. */
export function burn(P: Particles, a: number, m: number, era: number, intensity: number, dt: number, q: number, acc: Float32Array, heavy = false) {
  const k = sizeOf(m), r = (7 + 14 * m) * k;
  const rate = (i: number, perSec: number) => {
    acc[i] += perSec * intensity * q * dt;
    const n = Math.floor(acc[i]);
    acc[i] -= n;
    return n;
  };
  const [sc0, sc1] = smokeColors(era);
  for (let n = rate(0, 22 + 40 * m); n > 0; n--) {
    // many small tongues, clustered towards the middle, leaning and swaying: they read as one fire
    const fx = rr(-r, r) * rr(0.25, 1), fy = rr(-r, r) * rr(0.25, 1) * 0.8;
    P.spawn({ a, sprite: S_FLAME, layer: L_FIRE, flags: F_BASE | F_FLICKER, life: rr(0.35, 0.8), x: fx, y: fy, vx: rr(-6, 6), vz: rr(10, 22),
      s0: rr(4.5, 8.5) * k, s1: rr(1.5, 3.5) * k, rot: rr(-0.3, 0.3), c0: [1, 0.84, 0.5, 0.95], c1: FIRE_END });
  }
  for (let n = rate(1, 1.6); n > 0; n--) {
    P.spawn({ a, sprite: S_GLOW, layer: L_FIRE, fade: FADE_BUMP, life: 1.4, z: 2, s0: 44 * k, s1: 52 * k, c0: [1, 0.4, 0.1, 0.55] });
  }
  for (let n = rate(2, 2 + 4 * m); n > 0; n--) {
    const powder = era <= ERA_POWDER;
    P.spawn({ a, sprite: pick(SMOKES), layer: L_SMOKE, flags: F_WIND, fade: FADE_PUFF, life: rr(5, 9), x: rr(-r, r) * 0.6, y: rr(-r, r) * 0.6,
      z: 8 * k, vz: rr(10, 20) * k, drag: 0.4, s0: 12 * k, s1: rr(46, 70) * k, rot: rr(0, 6.28), vr: rr(-0.25, 0.25),
      c0: powder ? [0.28, 0.26, 0.24, 0.6] : [0.1, 0.09, 0.085, 0.7], c1: powder ? [0.42, 0.4, 0.38, 0] : sc1 });
    void sc0;
  }
  for (let n = rate(3, 4 + 10 * m); n > 0; n--) {
    P.spawn({ a, sprite: S_EMBER, layer: L_LIGHT, flags: F_FLICKER | F_WIND, life: rr(1, 2.4), x: rr(-r, r), y: rr(-r, r) * 0.8, z: 4,
      vx: rr(-10, 10), vy: rr(-10, 10), vz: rr(14, 36), drag: 1, grav: 2, s0: 3, s1: 1.2, c0: EMBER, c1: EMBER_END });
  }
  // shells still falling on the place now and then
  if (era > ERA_BLADE) {
    for (let n = rate(4, heavy ? 0.6 + 1.2 * m : 0.1 + 0.4 * m); n > 0; n--) blastSmall(P, a, m * 0.7, q, rr(-r, r) * 1.8, rr(-r, r) * 1.8, 0);
  }
}
